import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button, Column, Label, Row, Typography,
} from '@tempmee/react';
import {
  getCycleStatusSeverity,
  hasActiveFilters,
  useCycle,
  useCycleMembershipCount,
  useGetCycleMemberships,
  useUpdateMembershipStatus,
} from '../../../domain/Awards';
import useClampedPage from '../../../hooks/useClampedPage';
import { DsPagination } from '../../commonComponents/DsPagination';
import { MembershipStatusSelect } from '../../commonComponents/MembershipStatusSelect';
import { BestInDentalBreadcrumbs } from './components/BestInDentalBreadcrumbs';
import { CycleFormDialog } from './components/CycleFormDialog';
import { CycleSummary } from './components/CycleSummary';
import { MembershipFilters } from './components/MembershipFilters';
import { MembershipTable } from './components/MembershipTable';
import { UploadUsersDialog } from './components/UploadUsersDialog';

export const CycleDetail = () => {
  const { cycleId } = useParams();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', userType: '', status: '' });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const { cycle, isPending: isCyclePending, isError: isCycleError } = useCycle(cycleId);
  const {
    memberships,
    pageSize,
    totalCount,
    isPending: isRosterPending,
    isError: isRosterError,
  } = useGetCycleMemberships(cycleId, filters, page);

  // A status change on a filtered roster can drop the total below the current
  // page (e.g. moving the last nominee off a "Nominee"-filtered page 2), which
  // would otherwise strand it on an empty slice. Pull it back into range. A
  // failed fetch has no total (nullish → never clamp) — clamping on its 0
  // fallback would silently reset the page and refetch over the error state.
  useClampedPage(page, isRosterError ? null : totalCount, pageSize, setPage);

  // Only a complete cycle's Edit control depends on emptiness, so the count is
  // fetched for those alone — see useCycleMembershipCount for why the filtered
  // roster above can't stand in for it.
  const isComplete = cycle?.status === 'complete';
  const {
    count: membershipCount,
    isPending: isCountPending,
    isError: isCountError,
  } = useCycleMembershipCount(cycleId, { enabled: isComplete });

  // Rejections surface through the hook's default toast, which relays the
  // server's admin-facing message; per-row pending state rides along too.
  const updateStatus = useUpdateMembershipStatus(cycleId);

  // Page 3 of an unfiltered list is rarely a valid page of a filtered one, so
  // any filter change resets pagination back to the first page.
  const handleFiltersChange = (next) => {
    setFilters(next);
    setPage(1);
  };

  // The roster is fetched already filtered, so an empty page means one of two
  // things and the message must not conflate them: a cycle with no users at all
  // vs. a filter that happens to match none of the users it does have (GOTU-6400).
  const filtersActive = hasActiveFilters(filters);

  if (isCyclePending) {
    return (
      <Column px={8} pt={6} gap={5}>
        <Typography className="text-text-secondary">Loading cycle…</Typography>
      </Column>
    );
  }

  // Two different dead ends, kept distinct: a 404 resolves to null and means the
  // id is gone or mistyped, while a 403/500 is a genuine failure that must not
  // be reported as "not found". Either way, always leave a way back.
  if (isCycleError || !cycle) {
    return (
      <Column px={8} pt={6} gap={4}>
        <BestInDentalBreadcrumbs />
        <Typography>
          {isCycleError ? 'Could not load this cycle.' : 'Cycle not found.'}
        </Typography>
      </Column>
    );
  }

  // A complete cycle normally locks Edit too, but an empty one is a mistyped-date
  // rescue: awards-service (GOTU-5626) re-dates it back to active, so Edit stays
  // available while the roster-mutating controls do not. Anything short of a
  // confirmed-empty count (still loading, or a failed count) keeps Edit hidden
  // rather than risk offering an edit the server would reject.
  const isEmptyCompleteCycle = isComplete
    && !isCountPending && !isCountError && membershipCount === 0;
  const canEditCycle = !isComplete || isEmptyCompleteCycle;

  return (
    <Column px={8} pt={4} pb={12} gap={5}>
      <Column gap={3}>
        <BestInDentalBreadcrumbs cycleName={cycle.name} />

        <Row align="center" justify="between" gap={4}>
          <Row align="center" gap={3} className="min-w-0">
            <Typography variant="h5" as="h1" numberOfLines={1}>
              {cycle.name}
            </Typography>
            <Label severity={getCycleStatusSeverity(cycle.status)} variant="standard">
              {cycle.statusLabel}
            </Label>
          </Row>

          {/* Upload upserts the roster, so it follows isRosterMutable and is
              gone on any complete cycle. Edit is separate: it survives on an
              empty complete cycle as the only way back from a mistyped date,
              and offering Upload there would just re-lock what Edit unlocked. */}
          {cycle.isRosterMutable || canEditCycle ? (
            <Row gap={2} className="shrink-0">
              {cycle.isRosterMutable ? (
                <Button variant="outlined" onClick={() => setIsUploadOpen(true)}>
                  Upload users
                </Button>
              ) : null}
              {canEditCycle ? (
                <Button onClick={() => setIsEditOpen(true)}>Edit</Button>
              ) : null}
            </Row>
          ) : null}
        </Row>
      </Column>

      <CycleSummary cycle={cycle} />

      <MembershipTable
        toolbar={(
          <MembershipFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            totalCount={totalCount}
          />
        )}
        footer={(
          <DsPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
          />
        )}
        memberships={memberships}
        isPending={isRosterPending}
        isError={isRosterError}
        isEditable={cycle.isRosterMutable}
        emptyMessage={
          filtersActive
            ? 'No users match these filters.'
            : 'No users in this cycle yet.'
        }
        renderStatus={(memberRow) => (
          <MembershipStatusSelect
            row={memberRow}
            disabled={updateStatus.isRowPending(memberRow.userId)}
            onChange={(status) => updateStatus.editStatus(memberRow.userId, status)}
          />
        )}
      />

      <CycleFormDialog
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        cycle={cycle}
      />

      <UploadUsersDialog
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        cycleId={cycle.id}
        cycleName={cycle.name}
      />
    </Column>
  );
};
